import { useNavigation } from '@react-navigation/native';
import * as FileSystem from 'expo-file-system/legacy';
import cloneDeep from 'lodash.clonedeep';
import mime from 'mime';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, FlatList, Platform } from 'react-native';
import { useToast } from 'react-native-toast-notifications';
import uuid from 'react-native-uuid';

import { ModalHeader } from '@components/Headers/ModalHeader';
import Contact, { ContactType } from '@components/Invite/Contact';
import { Box, Text } from '@components/Restyle';
import ActivityIndicatorLoading from '@components/shared/ActivityIndicatorLoading';
import {
  ListLoadingFooter,
  MarginTopL,
  MarginTopM,
} from '@components/shared/ListSpacers';
import SearchInput from '@components/shared/SearchInput/SearchInput';
import { ChatDrawerScreen } from '@components/Web/Drawer/WebDrawerContext';
import {
  Chat,
  Document,
  DraftMessageAttributes,
  GetDraftMessageDocument,
  GetDraftMessageQuery,
  ListChatPaginationIdentifiersForwardDocument,
  ListChatPaginationIdentifiersForwardQuery,
  ListMessagesDocument,
  ListMessagesQuery,
  LocalFile,
  Message,
  MessageProject,
  MessageTask,
  Project,
  Task,
  useGetTaskQuery,
  useListChatPaginationIdentifiersForwardQuery,
  useSearchChatsQuery,
  useUpdateDraftMessageMutation,
} from '@graphql/generated';
import { ActiveChat } from '@hooks/useActiveChat';
import { useApolloClient } from '@hooks/useApolloClient';
import { useChatUploadManager } from '@hooks/useChatUploadManager';
import { useDebouncedSearch } from '@hooks/useDebouncedSearch';
import useMe from '@hooks/useMe';
import { useSelected } from '@hooks/useSelected';
import useSendMessage from '@hooks/useSendMessage';
import { useAppNavigation } from '@navigation/useAppNavigation';
import { useNetInfo } from '@src/context/NetInfoContext';
import { fetchWithErrorHandling } from '@utils/fetchWithErrorHandling';
import { logError } from '@utils/logNonFatalError';
import { AsyncStorage } from '@utils/storage';
import {
  isDocumentAttachment,
  isProjectAttachment,
  isTaskAttachment,
} from '@utils/tagCollectionService';

interface ForwardMessageContentProps {
  messageData:
    | Message
    | { message: null }
    | null
    | undefined
    | {
        attachments: Document[];
        body: string;
        isFromProjectMedia: boolean;
      };
  onCancelPress?: () => void;
  onForwardCompleted: () => void;
  taskId?: string;
}

export const ForwardMessageContent: React.FC<ForwardMessageContentProps> = ({
  messageData,
  onCancelPress,
  onForwardCompleted,
  taskId,
}) => {
  const toast = useToast();
  const { isConnected } = useNetInfo();
  const navigation = useNavigation();
  const { navigateToChatDrawer } = useAppNavigation();
  const [allChats, setAllChats] = useState<
    (ContactType & {
      isGroupChat: boolean;
    })[]
  >();
  const [filteredChats, setFilteredChats] = useState<
    (ContactType & {
      isGroupChat: boolean;
    })[]
  >();
  const { t } = useTranslation('shared');
  const [search, setSearch] = useState<string | undefined>(undefined);
  const [showLoading, setShowLoading] = useState<boolean>(false);
  const [selectedChats, toggleSelectedChat] = useSelected<
    ContactType & {
      isGroupChat: boolean;
    }
  >();
  const { client } = useApolloClient();
  const [updateDraftMessage] = useUpdateDraftMessageMutation();

  const { directUploadFiles } = useChatUploadManager();

  const [nextCursor, setCursor] = useState<string | null | undefined>(null);
  const isFetchingMoreRef = useRef(false);
  const [searchNextCursor, setSearchCursor] = useState<string | null>(null);
  const isFetchingMoreSearchRef = useRef(false);
  const flatListRef = useRef<FlatList>(null);

  // useSearchChatsQuery below is reactive (driven by its `term` variable, not
  // an imperative call), so there's no side-effecting search executor to
  // hand the hook — the query re-fires on its own whenever gatedTerm changes
  // (TA-10169).
  const { gatedTerm, hasSearched, isBelowThreshold, isPending } =
    useDebouncedSearch(search ?? '', () => {});

  // loadMoreSearch (below) is redefined every render and is not wrapped in
  // useCallback, so a `gatedTerm` read inside its `.then()` callback would
  // only ever compare a value to itself (the closure that created the
  // callback and the closure the `.then()` runs in are the same one) — never
  // detecting that the user moved on to a different term while the request
  // was in flight. A ref updated on every render always holds the CURRENT
  // gatedTerm by the time the promise resolves, regardless of which render's
  // closure the `.then()` came from (TA-10169).
  const latestGatedTermRef = useRef(gatedTerm);
  latestGatedTermRef.current = gatedTerm;

  const { me } = useMe();

  const afterComplete = (
    isNew: boolean,
    data: ListChatPaginationIdentifiersForwardQuery
  ) => {
    const edges = data.listChatsPagination.edges;
    if (edges) {
      const newEdges = edges.filter((edge) => edge?.node);
      const newChats = newEdges?.map((edge) => {
        const c = edge?.node as Chat;
        const otherUser =
          !c.isGroupChat && c.users
            ? c.users.find((user) => user.id !== me?.id)
            : undefined;
        const isSelfChat =
          !c.isGroupChat && !!me?.id && !!c.users?.length && !otherUser;
        const groupName = c.name?.trim();
        return {
          displayName: c.name
            ? c.name
            : t('myDisplayName', { displayName: me?.name }),
          id: c.id,
          initials: c.name.charAt(0),
          avatar: isSelfChat ? me?.avatar : c.avatar,
          isGroupChat: c.isGroupChat,
          isSelfChat,
          avatarUserId: isSelfChat ? me?.id : undefined,
          groupColorSeed: c.isGroupChat && groupName ? groupName : undefined,
        } as ContactType & {
          isGroupChat: boolean;
        };
      });

      if (isNew) {
        setAllChats(newChats);
      } else {
        setAllChats((prev) => {
          const map = new Map<string, ContactType & { isGroupChat: boolean }>();
          prev?.forEach((c) => c?.id && map.set(c.id, c));
          newChats?.forEach((c) => c?.id && map.set(c.id, c));
          return Array.from(map.values());
        });
      }
    }
    const pageInfo = data.listChatsPagination.pageInfo;
    if (pageInfo.hasNextPage) {
      setCursor(pageInfo.endCursor);
    } else {
      setCursor(null);
    }
  };

  const loadMore = () => {
    // Guard client before setting isFetchingMoreRef — if client is absent and
    // we used optional chaining, ?.query() returns undefined, .then() throws,
    // and the ref is never reset, permanently wedging pagination.
    if (!client || !nextCursor || isFetchingMoreRef.current) return;
    isFetchingMoreRef.current = true;
    // fetchMore is unsafe on no-cache queries in Apollo Client 3 — it tries
    // to merge into cache, which silently fails. Use client.query directly so
    // subsequent pages are always fetched without touching the cache.
    client
      .query<ListChatPaginationIdentifiersForwardQuery>({
        query: ListChatPaginationIdentifiersForwardDocument,
        variables: { first: 20, after: nextCursor },
        fetchPolicy: 'no-cache',
      })
      .then((res) => {
        afterComplete(false, res.data);
      })
      .finally(() => {
        isFetchingMoreRef.current = false;
      });
  };

  const { loading, data: dataPagination } =
    useListChatPaginationIdentifiersForwardQuery({
      variables: {
        first: 20,
        after: null,
      },
      fetchPolicy: 'no-cache',
    });

  useEffect(() => {
    if (dataPagination) afterComplete(true, dataPagination);
  }, [dataPagination]);

  const {
    data: searchData,
    loading: searchLoading,
    fetchMore: fetchMoreSearch,
  } = useSearchChatsQuery({
    variables: { term: gatedTerm, first: 20 },
    skip: !gatedTerm,
    fetchPolicy: 'cache-and-network',
  });

  const loadMoreSearch = () => {
    if (searchNextCursor && !isFetchingMoreSearchRef.current) {
      isFetchingMoreSearchRef.current = true;
      // Reuses gatedTerm (the already-settled, trimmed term from
      // useDebouncedSearch) rather than the raw live search state so page 2+
      // can't drift onto a different, unsettled term than page 0 (TA-10169).
      const termAtRequest = gatedTerm;
      fetchMoreSearch({
        variables: { term: termAtRequest, first: 20, after: searchNextCursor },
      })
        .then((res) => {
          // Compare against the ref (always current), not the `gatedTerm`
          // closed over above — that closure is fixed at call time and would
          // never differ from itself once the promise resolves (TA-10169).
          if (termAtRequest !== latestGatedTermRef.current) return;
          const edges = res.data?.searchChats?.edges;
          if (edges) {
            const newChats = edges
              .filter((edge) => edge?.node)
              .map((edge) => {
                const c = edge!.node as Chat;
                const otherUser =
                  !c.isGroupChat && c.users
                    ? c.users.find((user) => user.id !== me?.id)
                    : undefined;
                const isSelfChat =
                  !c.isGroupChat && !!me?.id && !!c.users?.length && !otherUser;
                const groupName = c.name?.trim();
                return {
                  displayName: c.name
                    ? c.name
                    : t('myDisplayName', { displayName: me?.name }),
                  id: c.id,
                  initials: c.name.charAt(0),
                  avatar: isSelfChat ? me?.avatar : c.avatar,
                  isGroupChat: c.isGroupChat,
                  isSelfChat,
                  avatarUserId: isSelfChat ? me?.id : undefined,
                  groupColorSeed:
                    c.isGroupChat && groupName ? groupName : undefined,
                } as ContactType & { isGroupChat: boolean };
              });
            setFilteredChats((prev) => {
              const map = new Map<
                string,
                ContactType & { isGroupChat: boolean }
              >();
              prev?.forEach((c) => c?.id && map.set(c.id, c));
              newChats.forEach((c) => c?.id && map.set(c.id, c));
              return Array.from(map.values());
            });
          }
          const pageInfo = res.data?.searchChats?.pageInfo;
          setSearchCursor(
            pageInfo?.hasNextPage ? (pageInfo.endCursor ?? null) : null
          );
        })
        .finally(() => {
          isFetchingMoreSearchRef.current = false;
        });
    }
  };

  const { sendDraftMessage } = useSendMessage();

  const { data: taskData } = useGetTaskQuery({
    variables: {
      id: taskId,
    },
    skip: !taskId,
  });

  const { getTask: task } = taskData || {};

  const downloadImage = async (uri: string, doc1: Document) => {
    const clientId = uuid.v4().toString();
    let ext =
      doc1?.name?.split('.').pop() ??
      mime.getExtension(doc1?.contentType ?? '');
    if (!ext) ext = 'bin';

    const fileName = `${clientId}.${ext}`;
    // Not a dynamic import('react-native-fs'): in dev that is fetched as a
    // lazy bundle and fails with LoadBundleFromServerRequestError (TA-11049).
    const localUri = `${FileSystem.documentDirectory}${fileName}`;
    await FileSystem.downloadAsync(uri, localUri);

    const fileInfo = await FileSystem.getInfoAsync(localUri);
    if (fileInfo.exists) {
      return localUri;
    }

    return null;
  };

  const getBase64ImageFromUrl = async (imageUrl: string) => {
    const res = await fetchWithErrorHandling(imageUrl);
    const blob = await res.blob();

    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.addEventListener(
        'load',
        function () {
          resolve(reader.result);
        },
        false
      );

      reader.onerror = () => {
        return reject(new Error('Error: getBase64ImageFromUrl'));
      };
      reader.readAsDataURL(blob);
    });
  };

  const convertToLocalFileDocument = async (
    attachments: Document[],
    draftMessageId: string
  ) => {
    const newArray = new Array<Document>();
    await Promise.all(
      attachments.map(async (xdoc1: any) => {
        const doc1 = xdoc1 as Document;
        const url =
          (await AsyncStorage.getItem(doc1.clientId)) || doc1.file.url;
        if (url) {
          return Platform.OS === 'web'
            ? getBase64ImageFromUrl(url)
            : downloadImage(url, doc1);
        }
        return null;
      })
    )
      .then((fileurls) => {
        for (let x = 0; x < fileurls.length; x++) {
          const xitem = attachments[x] as Document;
          const copyItem = cloneDeep(xitem);
          copyItem.file.url = fileurls[x];
          newArray.push(copyItem);
        }
      })
      .catch((err) => {
        console.log('error when forward file to chat', err);
        Alert.alert(
          'Error happened when get file to forward to chat, Please try again.'
        );
      });

    const newArrayDocument = new Array<Document>();
    for (const lf of newArray) {
      const xclientId = uuid.v4().toString();
      const temp = {
        __typename: 'LocalFile',
        id: xclientId,
        name: lf.name,
        size: lf.size,
        clientId: xclientId,
        url: lf.file.url,
        cdnBaseUrl: '',
        path: '',
        contentType: lf.contentType,
        isImage: lf.isImage,
        isAudio: lf.isAudio,
        duration: lf.duration,
        isPreviewable: lf.file.isPreviewable,
        thumbnail: lf.file.thumbnail,
        blobId: '',
      };

      const uploaded = await directUploadFiles([temp as LocalFile]);
      temp.blobId = uploaded?.[0]?.blobId;

      const doc: Document = {
        __typename: 'Document',
        id: xclientId,
        messageClientId: draftMessageId,
        messageCreateYn: false,
        name: temp.name,
        clientId: xclientId,
        contentType: lf.contentType,
        isImage: temp.isImage,
        size: temp.size,
        file: {
          ...temp,
          __typename: 'File',
          id: xclientId,
          cdnBaseUrl: '',
          url: temp.url,
        },
        isAudio: temp.isAudio,
        duration: temp.duration,
        isPreviewable: temp.isPreviewable,
        thumbnail: temp.thumbnail,
        blobId: temp.blobId ?? '',
      };
      newArrayDocument.push(doc);
    }

    return newArrayDocument;
  };

  const checkData = (selectedChat?: ContactType) => {
    if (!messageData) {
      toast.show('Cache data may be lost', {
        duration: 3000,
      });
      return false;
    }

    if (!me) {
      toast.show('Please login again', {
        duration: 3000,
      });
      return false;
    }

    if (!selectedChat) return false;

    return true;
  };

  // Warms the destination chat's message list before injecting the optimistic
  // draft message, so sendDraftMessage's cache write has an existing
  // ListMessages observer to attach to instead of racing a first-ever fetch
  // (carried over from the previous single-recipient refetch()-then-send
  // sequencing). Uses client.query directly — this now runs once per
  // recipient in a loop, so it can't be a query hook.
  const warmMessagesCache = (chatId: string) => {
    if (!client) return Promise.resolve();
    return client.query<ListMessagesQuery>({
      query: ListMessagesDocument,
      variables: { chatId, first: 20 },
      fetchPolicy: 'network-only',
    });
  };

  // TA-10299: forwards to exactly one recipient and returns once that
  // recipient's send has actually completed — the caller loops this over
  // every selected recipient instead of firing on every row tap. Navigation
  // into the destination chat's detail view only fires when the caller says
  // so (shouldNavigate), since "land inside chat X" only makes sense when
  // there's a single destination.
  const forwardMessageToChat = async (
    selectedChat: ContactType,
    shouldNavigate: boolean
  ) => {
    // make call to forward messageData

    const isOK = checkData(selectedChat);
    if (!isOK) {
      throw new Error('forward-precondition-failed');
    }

    const {
      attachments,
      body = '',
      isFromProjectMedia = false,
      isFromImagePreview = false,
    } = messageData;

    const draftMessageId = uuid.v4().toString();

    if (isFromImagePreview) {
      const draftMessage = {
        __typename: 'Message' as const,
        id: draftMessageId,
        chatId: selectedChat.id,
        authorId: me.id,
        clientId: draftMessageId,
        body,
        attachments: [
          ...((await convertToLocalFileDocument(attachments, draftMessageId)) ||
            []),
        ],
        author: me,
        createdAt: new Date().toISOString(),
        publishedAt: false,
        publish: false,
        isSender: true,
        chat: {
          ...selectedChat,
        },
        replyId: null,
        replyMessage: null,
        isDraft: true,
      };

      sendDraftMessage({ ...draftMessage });

      if (shouldNavigate && Platform.OS !== 'web') {
        navigateToChatDrawer({
          screen: ChatDrawerScreen.details,
          chatId: selectedChat.id,
        });
      }
    } else if (isFromProjectMedia) {
      const params1 = {
        chatId: selectedChat.id,
        body: '',
        tagsCollection: [
          {
            tasks: [task],
            project: task?.project,
            author: me,
          },
        ],
        localFiles: [],
        replyMessage: null,
      };

      await new Promise<void>((resolve, reject) => {
        updateDraftMessage({
          variables: { chatId: selectedChat.id, attributes: params1 },
          onCompleted: () => {
            const toLocalFiles = attachments.map((doc: Document) => {
              const {
                file,
                clientId,
                contentType,
                duration,
                id,
                isAudio,
                isImage,
                name,
                size,
              } = doc;

              const result: LocalFile = {
                __typename: 'LocalFile',
                id,
                name,
                size,
                clientId,
                url: file.url,
                cdnBaseUrl: file.cdnBaseUrl,
                path: file.path,
                contentType: contentType!,
                isImage,
                duration: duration ?? null,
                isAudio: isAudio ?? false,
                thumbnail: file.thumbnail || '',
                isPreviewable: file.isPreviewable,
              };
              return result;
            });
            const { getDraftMessage: draftMessage } =
              client?.cache?.readQuery<GetDraftMessageQuery>({
                query: GetDraftMessageDocument,
                variables: {
                  chatId: selectedChat.id,
                },
              }) || {
                getDraftMessage: {
                  chatId: selectedChat.id,
                  body,
                  localFiles: [],
                },
              };

            const params: DraftMessageAttributes = {
              ...draftMessage,
              body: '',
              localFiles: [
                ...(draftMessage?.localFiles ?? []),
                ...toLocalFiles,
              ],
              tagsCollection: [
                {
                  tasks: [task],
                  project: task?.project,
                  author: me,
                },
              ],
              replyMessage: null,
            };

            updateDraftMessage({
              variables: { chatId: selectedChat.id, attributes: params },
              onCompleted: () => {
                if (shouldNavigate && Platform.OS !== 'web') {
                  navigateToChatDrawer({
                    screen: ChatDrawerScreen.details,
                    chatId: selectedChat.id,
                  });
                }
                resolve();
              },
              onError: (e) => {
                console.log('err updateDraftMessage', e);
                reject(e);
              },
            });
          },
          onError: (e) => {
            console.log('err updateDraftMessage', e);
            reject(e);
          },
        });
      });
    } else {
      const messageProjects = attachments?.filter(isProjectAttachment);
      const messageTasks = attachments?.filter(isTaskAttachment);
      const messageDocs = attachments
        ?.filter(isDocumentAttachment)
        .map((item: any) => ({
          ...item,
          blobId: item.blobId ?? '',
          file: {
            ...(item.file ?? {}),
            path: item.file?.path ?? '',
          },
        }));
      const projects = messageProjects?.map(
        (messageproject: { project: any }) => messageproject.project
      );
      const tasks = messageTasks?.map(
        (messageTask: { task: any }) => messageTask.task
      );
      const projectIds = messageProjects?.map(
        (messageProject: { project: { id: any } }) =>
          messageProject?.project?.id
      );
      const taskIds = messageTasks?.map(
        (messageTask: { task: { id: any } }) => messageTask?.task?.id
      );

      const chatMessageTasks = tasks.map(
        (item: Task, index: any): MessageTask => ({
          __typename: 'MessageTask',
          id: `${draftMessageId}-${index}`,
          authorId: me.id,
          author: me,
          taskId: item.id,
          task: item,
        })
      );

      const chatMessageProjects = projects.map(
        (item: Project, index: any): MessageProject => ({
          __typename: 'MessageProject',
          id: `${draftMessageId}-${index}`,
          authorId: me.id,
          author: me,
          projectId: item.id,
          project: item,
        })
      );

      const draftMessage = {
        __typename: 'Message' as const,
        id: draftMessageId,
        chatId: selectedChat?.id,
        forwardingUserId: me?.id,
        authorId: me?.id,
        clientId: draftMessageId,
        body,
        tasks,
        taskIds,
        projects,
        projectIds,
        attachments: [
          ...(chatMessageProjects || []),
          ...(chatMessageTasks || []),
          ...(messageDocs || []),
        ],
        author: me,
        createdAt: new Date().toISOString(),
        publishedAt: false,
        publish: false,
        isSender: true,
        readAt: null,
        readReceipts: null,
        chat: {
          ...selectedChat,
        },
        replyId: null,
        replyMessage: null,
        isDraft: true,
        isRetry: !isConnected,
        checkin: messageData?.checkin ?? null,
      };

      sendDraftMessage(draftMessage);

      if (shouldNavigate) {
        if (Platform.OS === 'web') {
          navigateToChatDrawer({
            screen: ChatDrawerScreen.details,
            chatId: '',
            activeChat: selectedChat as ActiveChat,
          });
        } else {
          navigateToChatDrawer({
            screen: ChatDrawerScreen.details,
            chatId: selectedChat?.id,
            resetStack: true,
          });
        }
      }
    }
  };

  // TA-10299: the only place that actually sends. Recipients are only ever
  // forwarded to once this fires — tapping a row in the list below just
  // toggles selection now, it never sends anything on its own.
  const handleForwardPress = async () => {
    if (showLoading || selectedChats.length === 0) return;

    setShowLoading(true);
    const chatsToForward = selectedChats;
    // Only makes sense to land the user inside a specific chat's detail view
    // when there's exactly one destination — with multiple recipients there's
    // no single "the" chat to navigate into.
    const shouldNavigate = chatsToForward.length === 1;
    let succeededCount = 0;
    let hadFailure = false;

    try {
      for (const chat of chatsToForward) {
        try {
          await warmMessagesCache(chat.id);
          await forwardMessageToChat(chat, shouldNavigate);
          succeededCount += 1;
          // Deselect as each recipient succeeds. This closure's own
          // `toggleSelectedChat`/`selectedChats` are fixed for this entire
          // handleForwardPress run (a new closure is only created on the
          // NEXT render, which this already-running call never sees) — so
          // calling it more than once here is only safe because
          // useSelected.toggleSelected uses a functional setState updater
          // throughout (QA caught this in TA-10299: the previous
          // non-functional removal branch let a later call in this same
          // loop overwrite an earlier one and re-select an already-sent
          // recipient). A failed recipient is left selected so pressing
          // Forward again retries just that one instead of re-sending (and
          // duplicating) to ones that already went through.
          toggleSelectedChat(chat);
        } catch (e) {
          if ((e as Error)?.message === 'forward-precondition-failed') {
            // checkData() already surfaced its own toast, and this
            // precondition (missing messageData/me) would fail identically
            // for every remaining recipient — stop instead of repeating it.
            break;
          }
          hadFailure = true;
          logError({
            message: 'Forward message to chat failed',
            errorMessage: e instanceof Error ? e.message : String(e),
            operationName: 'forwardMessageToChat',
          });
        }
      }

      if (succeededCount > 0 && !hadFailure) {
        toast.show('Forwarded', { duration: 3000 });

        if (Platform.OS === 'web') {
          onForwardCompleted();
        } else if (!shouldNavigate) {
          navigation.goBack();
        }
      } else if (hadFailure) {
        toast.show(
          succeededCount > 0
            ? `Forwarded to ${succeededCount} of ${chatsToForward.length}. Please retry the rest.`
            : 'Something went wrong forwarding this message. Please try again.',
          { duration: 3000 }
        );
      }
    } finally {
      setShowLoading(false);
    }
  };

  useEffect(() => {
    if (isBelowThreshold) {
      setSearchCursor(null);
      return;
    }
    // hasSearched-gated: Apollo retains the previous response's `data` even
    // once the query stops re-firing below threshold — reading searchData
    // unguarded here would leak a stale search's chats in as if live
    // (TA-10169).
    if (!hasSearched) {
      // A new above-threshold term is mid-debounce (hasSearched briefly
      // resets to false between two settled terms). Clear the PREVIOUS
      // term's accumulated filteredChats/searchCursor now, so once
      // hasSearched flips back to true for the new term, stale rows can't
      // render as live/tappable underneath the spinner before the new
      // term's own searchData has actually landed (TA-10169).
      setFilteredChats([]);
      setSearchCursor(null);
      return;
    }
    const edges = searchData?.searchChats?.edges;
    if (!searchLoading && edges) {
      const newChats = edges
        .filter((edge) => edge?.node)
        .map((edge) => {
          const c = edge!.node as Chat;
          const otherUser =
            !c.isGroupChat && c.users
              ? c.users.find((user) => user.id !== me?.id)
              : undefined;
          const isSelfChat =
            !c.isGroupChat && !!me?.id && !!c.users?.length && !otherUser;
          const groupName = c.name?.trim();
          return {
            displayName: c.name
              ? c.name
              : t('myDisplayName', { displayName: me?.name }),
            id: c.id,
            initials: c.name.charAt(0),
            avatar: isSelfChat ? me?.avatar : c.avatar,
            isGroupChat: c.isGroupChat,
            isSelfChat,
            avatarUserId: isSelfChat ? me?.id : undefined,
            groupColorSeed: c.isGroupChat && groupName ? groupName : undefined,
          } as ContactType & { isGroupChat: boolean };
        });
      setFilteredChats(newChats);
      const pageInfo = searchData.searchChats.pageInfo;
      setSearchCursor(
        pageInfo.hasNextPage ? (pageInfo.endCursor ?? null) : null
      );
    }
    // me/t are read above (self-chat display name/avatar) — listed here so
    // this effect recomputes filteredChats if `me` loads/changes after the
    // search results already landed, or the language changes, rather than
    // leaving stale avatars/display names cached until some other dep
    // happens to change (TA-10169). useMe()'s own `me` is memoized on its
    // underlying fields, so this doesn't cause extra fires in production.
  }, [isBelowThreshold, hasSearched, searchData, searchLoading, me, t]);

  useEffect(() => {
    flatListRef.current?.scrollToOffset({ offset: 0, animated: false });
  }, [gatedTerm]);

  // hasSearched-gated so the previous term's search results (still sitting
  // in the filteredChats accumulator) can't render as live/tappable during
  // the pending window between a keystroke and the debounce settling for a
  // new above-threshold term (TA-10169).
  let visibleChats: (ContactType & { isGroupChat: boolean })[] | undefined;
  if (isBelowThreshold) {
    visibleChats = allChats;
  } else if (hasSearched) {
    visibleChats = filteredChats;
  } else {
    visibleChats = [];
  }

  return (
    <Box
      overflow='hidden'
      height='100%'
      pt={Platform.OS === 'web' ? 'xs' : 'xxs'}>
      <Box marginHorizontal='m'>
        <ModalHeader
          title='Forward'
          showCancel
          onCancel={() =>
            Platform.OS === 'web' ? onCancelPress() : navigation.goBack()
          }
          cancelColor='greenSecondary'
          onSubmit={handleForwardPress}
          submitLabel='Forward'
          disabled={selectedChats.length === 0 || showLoading}
        />
        <Box marginTop='xl' flexDirection='row' alignItems='center'>
          <SearchInput
            value={search}
            onTextChanged={(t: string) => setSearch(t)}
            onCancel={() => setSearch(undefined)}
          />
        </Box>
      </Box>
      <Box marginTop='m' backgroundColor='grey01'>
        <Text
          variant='labelEmphasized'
          marginVertical='xs'
          marginHorizontal='m'>
          All chats
        </Text>
      </Box>
      {(loading ||
        showLoading ||
        (!isBelowThreshold &&
          (searchLoading || isPending || !hasSearched))) && (
        <ActivityIndicatorLoading marginTop={125} top={125} />
      )}

      <FlatList
        showsVerticalScrollIndicator={false}
        ref={flatListRef}
        data={visibleChats}
        keyExtractor={(item) => item.id}
        onEndReached={() => {
          if (!isBelowThreshold) {
            loadMoreSearch();
          } else if (nextCursor) {
            loadMore();
          }
        }}
        ListHeaderComponent={MarginTopM}
        ListFooterComponent={
          <ListLoadingFooter
            isLoading={!isBelowThreshold ? !!searchNextCursor : !!nextCursor}
          />
        }
        renderItem={({ item }) => {
          return (
            <Contact
              contact={item}
              onPress={() => {
                if (showLoading) {
                  toast.show(
                    'Please wait until the message finishes forwarding',
                    {
                      duration: 3000,
                    }
                  );
                  return false;
                }

                toggleSelectedChat(item);
              }}
              isEditing
              isSelected={!!selectedChats.find((c) => c.id === item.id)}
              isContactGroup={item.isGroupChat}
              isChat={true}
              filterVal={search}
              isSelfChat={item.isSelfChat}
              avatarUserId={item.avatarUserId}
              groupColorSeed={item.groupColorSeed}
            />
          );
        }}
        ItemSeparatorComponent={MarginTopL}
      />
    </Box>
  );
};
